import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
// helpers
import _ from 'lodash';
import * as Yup from 'yup';
// mui
import {
  DialogContent,
  DialogActions,
  FormHelperText,
  Divider,
  Grid as MuiGrid,
  MenuItem as MuiMenuItem,
  Select as MuiSelect,
  useTheme,
  DialogTitle,
  Dialog,
  Box,
  InputAdornment,
  Grid,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CowbellModalIcon from '../../../../_assets/svg/CowbellModal.svg';

// components
import { withShowable } from '../../../../console/_global/lib/withShowable';
import { withFormController } from '../../../hocs/forms';
import CbButton from '../../../Buttons/CbButton';
import { TextFieldBase } from '../../../inputs/TextFieldBase';
import { InputLabelBase } from '../../../inputs/InputLabelBase';

// service
import {
  getBalanceOnPolicyInvoice,
  makePaymentFromCowbell,
} from '../../../../console/customers/_services/BillingService';
import { useAllow } from '../../../hooks/useAllow';

import { deriveCurrencyFormat } from '../../../../i18n/currencies';
import { useCowbellTranslations } from '../../../../i18n/translations';
import {
  getCurrencySymbolByProduct,
  getRegionByProduct,
} from '../../../../i18n/utils';
import {
  COUNTRY_MAPPING,
  languages,
} from '../../../../i18n/i18n.language-config';
import { ProductTypes } from '../../../../types';
import { manageAPIError } from '../../../../utils';
import { CustomNumericFormatField } from './CustomNumericFormatField';
import { REDACTED_FIELD } from '../../../../config/logrocket';
import { useAccountDetails } from '../../../hooks/queries/accounts/useAccountDetails';
import DotLoader from '../../../../console/workflows/sso/components/DotLoader';
import { hooks } from '@cowbell/ui';
import { usePersona } from '../../../hooks/usePersona';
import { updateAccountContact } from '../../../../accounts/AccountService';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import LanguageForm, {
  useLanguageFormState,
} from '../../../../i18n/forms/LanguageForm';
import { FormLanguageProvider } from '../../../../i18n/forms/FormLanguageProvider';

const HiddenGrid = withShowable(MuiGrid);
const HelperText = withShowable(FormHelperText);
const TextField = withFormController(TextFieldBase);

export const schema = Yup.object().shape({
  paymentType: Yup.string().ensure().required('Payment Type is required'),
  processorName: Yup.string().required('Processor Name is required'),
  paymentDesc: Yup.string(),
  paymentAmount: Yup.string()
    .required('Invalid Amount')
    .typeError('Payment Amount is required')
    // A lone '.' or '0' is a non-empty string but not an amount.
    .test('positive', 'Invalid Amount', (value) => Number(value) > 0),
});

const defaultFormValues = {
  paymentAmount: '',
  paymentDesc: '',
  paymentType: '',
  processorName: '',
};

export const ApplyExternalPayment = ({ data, close, ...props }: any) => {
  const notifications = useNotifications();
  const classes = useStyles();
  const theme = useTheme();
  const [paymentData, setPaymentData] = useState({});
  const [BEerror, setBEerror] = useState('');

  console.log({ data, props });

  const canApplyOrRefundPayment = useAllow([
    'role:admin',
    'policy:apply_payment',
  ]);

  const region = getRegionByProduct(data.product);
  const currencySymbol = getCurrencySymbolByProduct(data.product);
  const { t } = useCowbellTranslations();

  const paymentOptions = getLocalPaymentOptions(region);
  const processorOptions = getLocalProcessorOptions(region);

  const form = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(schema),
  });

  // @ts-expect-error TS(2339): Property 'amountDue' does not exist on type '{}'.
  const isRefund = paymentData && paymentData.amountDue < 0;

  const { reset: resetForm } = form;
  React.useEffect(() => {
    resetForm(defaultFormValues);
    setPaymentData({});

    getBalanceOnPolicyInvoice({ policyId: data.id })
      .then((resp: any) => {
        setPaymentData(resp.data);
        setBEerror('');
      })
      .catch((error: any) => {
        const errorMsg = manageAPIError(error);
        setBEerror(errorMsg);
      });
  }, [data, resetForm]);

  const formCompliance = () => {
    // @ts-expect-error TS(2339): Property 'cowbellCompliance' does not exist on typ... Remove this comment to see the full error message
    if (paymentData.cowbellCompliance) {
      return 'Cowbell Compliance';
    }
    return `${t('agency')} Compliance`;
  };

  const getbillNaming = () => {
    const agencyBilled = _.get(data, 'firmographicData.agencyBilled');
    if (agencyBilled === undefined) {
      return 'Direct Bill';
    }
    if (agencyBilled) {
      return `${t('agency')} Bill`;
    }
    return 'Direct Bill';
  };

  const invoiceStatement = () => {
    if (
      // @ts-expect-error TS(2339): Property 'cowbellCompliance' does not exist on typ... Remove this comment to see the full error message
      !paymentData.cowbellCompliance &&
      _.get(data, 'firmographicData.agencyBilled') === true
    ) {
      return '(without Commission, without Surplus Taxes)';
    }
    if (
      // @ts-expect-error TS(2339): Property 'cowbellCompliance' does not exist on typ... Remove this comment to see the full error message
      paymentData.cowbellCompliance &&
      _.get(data, 'firmographicData.agencyBilled') === true
    ) {
      return '(without Commission)';
    }
    return '';
  };

  const handleSelectPaymentType = (event: any) => {
    form.setValue('paymentType', event.target.value);
  };

  const handleSelectProcessorName = (event: any) => {
    form.setValue('processorName', event.target.value);
  };

  const onSubmit = (formData: any) => {
    const paymentAmount = Number(
      formData.paymentAmount.replace(/[^0-9.-]+/g, '')
    );

    const payload = {
      ...formData,
      accountId: data.accountId,
      policyId: data.id,
      customerEmail: data.customerEmail,
      paymentAmount: isRefund ? -Math.abs(paymentAmount) : paymentAmount,
    };

    return makePaymentFromCowbell({}, payload)
      .then(() => {
        notifications.success('Payment was made Successfully!');
        close();
        data?.onPaymentSuccessful?.();
        data?.refresh?.();
      })
      .catch((error: any) => {
        const errorMsg = manageAPIError(error);
        setBEerror(errorMsg);
      });
  };
  const buttonText = isRefund ? 'Refund' : 'Make Payment';

  const inputtedPaymentAmount = form.watch('paymentAmount');
  const pendingBalanceAmount = calculateBalance(
    // @ts-expect-error TS(2339): Property 'amountDue' does not exist on type '{}'.
    paymentData.amountDue,
    inputtedPaymentAmount || 0
  );

  return (
    <Dialog {...props}>
      <DialogTitle classes={{ root: classes.rootTitle }}>
        <Box className={classes.boxTitle}>
          <CowbellModalIcon />
          Payment/Refund for{' '}
          <span className={REDACTED_FIELD}>{data.companyName}</span>
        </Box>
        <CancelOutlinedIcon
          // @ts-expect-error TS(2339): Property 'cancel' does not exist on type 'ClassNam... Remove this comment to see the full error message
          className={classes.cancel}
          // @ts-expect-error TS(2769): No overload matches this call.
          fontSize="1.3333333333rem"
          onClick={close}
        />
      </DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogContent className={`modal-title ${classes.dialogStyle}`}>
            <MuiGrid
              container
              justifyContent="center"
              direction="column"
              className={classes.header}
            >
              <MuiGrid item md={12}>
                <h2>Policy# {data.policyNumber}</h2>
              </MuiGrid>
              <MuiGrid item md={12}>
                <h3>
                  {getbillNaming()}
                  {region !== languages['en-GB'] && ` | ${formCompliance()}`}
                </h3>
              </MuiGrid>
            </MuiGrid>

            <Divider className={classes.divider} />

            <MuiGrid container>
              <MuiGrid item md={9}>
                {/* @ts-expect-error TS(2339): Property 'totalPremium' does not */}
                {paymentData.totalPremium > 0
                  ? 'Total Premium'
                  : 'Premium Refund'}
              </MuiGrid>

              <MuiGrid
                item
                md={3}
                className={classes.textRight}
                style={
                  // @ts-expect-error TS(2339): Property 'totalPremium' does not exist on type '{}... Remove this comment to see the full error message
                  paymentData.totalPremium < 0
                    ? { color: theme.config.colors.ripeTomato }
                    : {}
                }
              >
                {deriveCurrencyFormat(
                  region,
                  // @ts-expect-error TS(2339): Property 'totalPremium' does not exist on type '{}... Remove this comment to see the full error message
                  Math.abs(paymentData.totalPremium || 0)
                )}
              </MuiGrid>
            </MuiGrid>

            <MuiGrid container>
              <MuiGrid item md={9}>
                <MuiGrid container>
                  <MuiGrid item md={12}>
                    {/* @ts-expect-error TS(2339): Property 'commission' does not */}
                    {paymentData.commission >= 0
                      ? 'Commissions'
                      : 'Commissions Refund'}
                  </MuiGrid>

                  <HiddenGrid
                    show={
                      data.isSurplus || data.product === ProductTypes.prime_one
                    }
                    item
                    md={12}
                  >
                    <HelperText className={classes.hiddenText}>
                      ({_.get(data, 'firmographicData.commission', 0)}% of
                      Commissionable Premium:{' '}
                      {deriveCurrencyFormat(
                        region,
                        // @ts-expect-error TS(2339): Property 'commissionablePremium' does not exist on... Remove this comment to see the full error message
                        Math.abs(paymentData.commissionablePremium)
                      )}
                      )
                    </HelperText>
                  </HiddenGrid>
                </MuiGrid>
              </MuiGrid>

              <MuiGrid
                item
                md={3}
                className={classes.textRight}
                style={
                  // @ts-expect-error TS(2339): Property 'commission' does not exist on type '{}'.
                  paymentData.commission < 0
                    ? { color: theme.config.colors.ripeTomato }
                    : {}
                }
              >
                {/* @ts-expect-error TS(2339): Property 'commission' does not */}
                {deriveCurrencyFormat(region, paymentData.commission)}
              </MuiGrid>
            </MuiGrid>

            <HiddenGrid show={data.isSurplus} container>
              <MuiGrid item md={9}>
                Surplus Taxes
              </MuiGrid>

              <MuiGrid
                item
                md={3}
                className={classes.textRight}
                style={
                  // @ts-expect-error TS(2339): Property 'surplusTaxes' does not exist on type '{}... Remove this comment to see the full error message
                  paymentData.surplusTaxes < 0
                    ? { color: theme.config.colors.ripeTomato }
                    : {}
                }
              >
                {/* @ts-expect-error TS(2339): Property 'commission' does not */}
                {deriveCurrencyFormat(region, paymentData.surplusTaxes)}
              </MuiGrid>
            </HiddenGrid>

            <Divider className={classes.divider} />
            <Divider
              className={classes.divider}
              style={{ margin: '0.25rem 0 0.5rem' }}
            />

            <MuiGrid container>
              <MuiGrid item md={9}>
                <MuiGrid container>
                  <MuiGrid item md={12}>
                    Invoice Amount
                  </MuiGrid>

                  <HiddenGrid show={invoiceStatement() !== ''} item md={12}>
                    <HelperText className={classes.hiddenText}>
                      {invoiceStatement()}
                    </HelperText>
                  </HiddenGrid>
                </MuiGrid>
              </MuiGrid>

              <MuiGrid item md={3} className={classes.textRight}>
                {/* @ts-expect-error TS(2339): Property 'invoiceAmount' does not exist on type '{... Remove this comment to see the full error message */}
                {deriveCurrencyFormat(region, paymentData.invoiceAmount)}
              </MuiGrid>
            </MuiGrid>

            <MuiGrid container>
              <MuiGrid item md={9}>
                Already Paid
              </MuiGrid>

              <MuiGrid
                item
                md={3}
                className={classes.textRight}
                style={
                  // @ts-expect-error TS(2339): Property 'alreadyPaid' does not exist on type '{}'... Remove this comment to see the full error message
                  paymentData.alreadyPaid < 0
                    ? { color: theme.config.colors.ripeTomato }
                    : {}
                }
              >
                {/* @ts-expect-error TS(2339): Property 'commission' does not */}
                {deriveCurrencyFormat(region, paymentData.alreadyPaid)}
              </MuiGrid>
            </MuiGrid>

            <Divider className={classes.divider} />
            <Divider
              className={classes.divider}
              style={{ margin: '0.25rem 0 0.5rem' }}
            />

            <MuiGrid container>
              <MuiGrid item md={5}>
                <InputLabelBase style={{ paddingTop: '0rem' }}>
                  {/* @ts-expect-error TS(2339): Property 'commission' does not */}
                  {paymentData.amountDue < 0 ? 'Over Paid' : 'Amount Due'}
                </InputLabelBase>
              </MuiGrid>

              <MuiGrid
                item
                md={7}
                className={classes.textRight}
                style={
                  // @ts-expect-error TS(2339): Property 'amountDue' does not exist on type '{}'.
                  paymentData.amountDue < 0
                    ? { color: theme.config.colors.ripeTomato }
                    : {}
                }
              >
                {/* @ts-expect-error TS(2339): Property 'commission' does not */}
                {deriveCurrencyFormat(region, paymentData.amountDue)}
              </MuiGrid>
            </MuiGrid>

            <HiddenGrid
              // @ts-expect-error TS(2339): Property 'amountDue' does not exist on type '{}'.
              show={parseFloat(paymentData.amountDue) > 0}
              container
              className={classes.textRight}
            >
              <MuiGrid item md={5}>
                <InputLabelBase required className={classes.label}>
                  Pay
                </InputLabelBase>
              </MuiGrid>

              <MuiGrid item md={7}>
                <TextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {currencySymbol}
                      </InputAdornment>
                    ),
                    inputComponent: CustomNumericFormatField,
                  }}
                  name="paymentAmount"
                  // @ts-expect-error TS(2322): Type 'Control<{ paymentAmount: string; paymentDesc... Remove this comment to see the full error message
                  controllerProps={{ control: form.control }}
                  disabled={!canApplyOrRefundPayment}
                />
              </MuiGrid>
            </HiddenGrid>
            <HiddenGrid
              // @ts-expect-error TS(2339): Property 'amountDue' does not exist on type '{}'.
              show={paymentData.amountDue < 0}
              container
              className={classes.textRight}
            >
              <MuiGrid item md={5}>
                <InputLabelBase required className={classes.label}>
                  Refund
                </InputLabelBase>
              </MuiGrid>

              <MuiGrid item md={7}>
                <TextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {currencySymbol}
                      </InputAdornment>
                    ),
                    inputComponent: CustomNumericFormatField,
                  }}
                  name="paymentAmount"
                  // @ts-expect-error TS(2322): Type 'Control<{ paymentAmount: string; paymentDesc... Remove this comment to see the full error message
                  controllerProps={{ control: form.control }}
                  disabled={!canApplyOrRefundPayment}
                />
              </MuiGrid>
            </HiddenGrid>
            <MuiGrid container>
              <MuiGrid item md={5}>
                Balance
              </MuiGrid>
              <MuiGrid item md={7} className={classes.textRight}>
                {deriveCurrencyFormat(region, pendingBalanceAmount)}
              </MuiGrid>
            </MuiGrid>
            <MuiGrid container style={{ margin: '2rem 0' }}>
              <MuiGrid item md={5}>
                <InputLabelBase required className={classes.label}>
                  Payment Type
                </InputLabelBase>
              </MuiGrid>
              <MuiGrid item md={7}>
                <MuiSelect
                  variant="standard"
                  name="paymentType"
                  {...form.register('paymentType')}
                  disabled={!canApplyOrRefundPayment}
                  required
                  onChange={handleSelectPaymentType}
                  classes={{
                    select: classes.select,
                    icon: classes.selectIcon,
                  }}
                  defaultValue={null}
                  displayEmpty
                  placeholder="please select a reason"
                  // @ts-expect-error TS(2339): Property 'errors' does not exist on type 'UseFormR... Remove this comment to see the full error message
                  error={form.errors?.paymentType?.message}
                >
                  {paymentOptions.map(renderOptions)}
                </MuiSelect>
                <FormHelperText className={classes.error}>
                  {/* @ts-expect-error TS(2339): Property 'commission' does not */}
                  {form.errors?.paymentType?.message}
                </FormHelperText>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid
              container
              className={classes.textRight}
              style={{ paddingBottom: '24px' }}
            >
              <MuiGrid item md={5}>
                <InputLabelBase required className={classes.label}>
                  Processor
                </InputLabelBase>
              </MuiGrid>
              <MuiGrid item md={7}>
                <MuiSelect
                  variant="standard"
                  name="processorName"
                  {...form.register('processorName')}
                  disabled={!canApplyOrRefundPayment}
                  required
                  onChange={handleSelectProcessorName}
                  classes={{
                    select: classes.select,
                    icon: classes.selectIcon,
                  }}
                  defaultValue={null}
                  displayEmpty
                  // @ts-expect-error TS(2339): Property 'errors' does not exist on type 'UseFormR... Remove this comment to see the full error message
                  error={form.errors?.processorName?.message}
                >
                  {processorOptions.map(renderOptions)}
                </MuiSelect>
                <FormHelperText className={classes.error}>
                  {/* @ts-expect-error TS(2339): Property 'commission' does not */}
                  {form.errors?.processorName?.message}
                </FormHelperText>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid container>
              <MuiGrid item md={5}>
                <InputLabelBase className={classes.label}>
                  Comments
                </InputLabelBase>
              </MuiGrid>
              <MuiGrid item md={7}>
                <TextField
                  type="text"
                  disabled={!canApplyOrRefundPayment}
                  name="paymentDesc"
                  multiline
                  minRows={4}
                  // @ts-expect-error TS(2322): Type 'Control<{ paymentAmount: string; paymentDesc... Remove this comment to see the full error message
                  controllerProps={{ control: form.control }}
                  placeholder="Details of payment"
                />
              </MuiGrid>
            </MuiGrid>
            {BEerror && (
              <FormHelperText className={classes.error}>
                {BEerror}
              </FormHelperText>
            )}
          </DialogContent>
          <DialogActions>
            <CbButton styleName="cancel" onClick={close} buttonText="Cancel" />
            <CbButton
              type="submit"
              styleName="ctaButton"
              loading={form.formState.isSubmitting}
              disabled={
                !canApplyOrRefundPayment ||
                form.formState.isSubmitting ||
                // @ts-expect-error TS(2339): Property 'errors' does not exist on type 'UseFormR... Remove this comment to see the full error message
                !!form.errors?.paymentAmount ||
                // @ts-expect-error TS(2339): Property 'amountDue' does not exist on type '{}'.
                paymentData.amountDue === 0
              }
            >
              {buttonText}
            </CbButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

const calculateBalance = (invoiceAmount: any, payment: any) => {
  const isRefund = invoiceAmount < 0;
  const total = isRefund
    ? parseFloat(invoiceAmount) + parseFloat(payment)
    : parseFloat(invoiceAmount) - parseFloat(payment);
  return total || 0;
};

const useStyles = makeStyles(({ config }) => ({
  header: {
    textAlign: 'center',
    margin: '0.5rem 0',
    '& h3': {
      margin: 0,
    },
    '& h2': {
      margin: 0,
    },
  },
  boxTitle: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    textOverflow: 'ellipsis',
  },
  rootTitle: {
    '& > h2': {
      maxWidth: '99%',
    },
  },
  hiddenText: {
    marginTop: '0rem',
    color: '#9ba0ae',
  },
  dialogStyle: {
    textAlign: 'left',
    padding: '1rem 5rem !important',
  },
  label: {
    padding: '0 ',
  },
  textRight: {
    textAlign: 'right',
    '& .MuiInputBase-input': {
      textAlign: 'right',
    },
  },
  error: {
    fontSize: config.textSizes.normal,
    marginTop: 0,
    textAlign: 'right',
    color: config.colors.textRed,
  },
  select: {
    justifyContent: 'right',
    '&:focus': {
      borderRadius: 5,
    },
  },
  selectIcon: {
    top: '50%',
    transform: 'translateY(-50%)',
  },
  divider: {
    marginTop: '0.5rem',
  },
  red: {
    color: config.colors.ripeTomato,
  },
}));

export const ApplyExternalPaymentConfig = {
  ApplyExternalPayment: {
    component: ApplyExternalPayment,
    config: {
      maxWidth: 'sm',
      minWidth: 'sm',
      override: true,
    },
  },
};

const paymentOptionsUS = [
  { label: 'Select a Payment Option...', value: null },
  { label: 'Check', value: 'Check' },
  { label: 'Credit Card', value: 'Credit Card' },
  { label: 'ACH/Wire Transfer', value: 'Wire' },
  { label: 'Bank Account', value: 'Bank Account' },
  { label: 'Refund/Credit', value: 'Refund/Credit' },
];

const paymentOptionsGB = [
  { label: 'Select a Payment Option...', value: null },
  { label: 'OneAdvent', value: 'OneAdvent' },
  { label: 'Refund/Credit', value: 'Refund/Credit' },
];

const paymentOptionsAU = [
  { label: 'Select a Payment Option...', value: null },
  { label: 'Refund/Credit', value: 'Refund/Credit' },
];

const processorOptionsGB = [
  { label: 'Select a Processor Option...', value: null },
  { label: 'Cowbell Credit', value: 'Cowbell Credit' },
  {
    label: 'Great American Chaucer',
    value: 'Great American Chaucer',
  },
  {
    label: 'Chaucer',
    value: 'Chaucer',
  },
];

const processorOptionsUS = [
  { label: 'Select a Processor Option...', value: null },
  { label: 'Boost - BLU', value: 'Boost - BLU' },
  { label: 'Boost - FLY', value: 'Boost - FLY' },
  { label: 'Palomar', value: 'Palomar' },
  { label: 'Surplus', value: 'Surplus' },
  { label: 'Obsidian', value: 'Obsidian' },
  { label: 'Skyward', value: 'Skyward' },
  { label: 'Cowbell Credit', value: 'Cowbell Credit' },
  { label: 'OneInc', value: 'OneInc' },
  { label: 'Benchmark', value: 'Benchmark' },
  { label: 'Chaucer', value: 'Chaucer' },
];

const processorOptionsAU = [
  { label: 'Select a Processor Option...', value: null },
  { label: 'Cowbell Credit', value: 'Cowbell Credit' },
  { label: 'Zurich', value: 'Zurich' },
];

const getLocalPaymentOptions = (region: any) => {
  if (region === languages['en-GB']) {
    return paymentOptionsGB;
  }

  if (region === languages['en-AU']) {
    return paymentOptionsAU;
  }

  return paymentOptionsUS;
};

const getLocalProcessorOptions = (region: any) => {
  if (region === languages['en-GB']) {
    return processorOptionsGB;
  }

  if (region === languages['en-AU']) {
    return processorOptionsAU;
  }

  return processorOptionsUS;
};

const renderOptions = ({ label, value }: any) => {
  return (
    <MuiMenuItem key={label} value={value}>
      {label}
    </MuiMenuItem>
  );
};
